import { useCallback, useEffect, useRef, useState } from "react";
import { getRecognitionPerDay } from "../utils/api";
import { useDashboardWebSocket } from "../contexts/DashboardWebSocketContext";
import { useDashboardLive } from "../contexts/DashboardLiveContext";

const RECOGNITION_PIE_WS_FALLBACK_MS = 8000;

const PIE_MESSAGE_TYPES = new Set([
  "user_recognition_pie_snapshot",
  "user_recognition_pie_update",
  "user_recognition_pie_error",
]);

function payloadMatchesActiveDate(payload, activeDate) {
  const range = payload?.date_range;
  if (!range || !activeDate) {
    return true;
  }
  const start = range.start_date;
  const end = range.end_date;
  if (start && end && start === end) {
    return start === activeDate;
  }
  return start === activeDate || end === activeDate;
}

export function useRecognitionPieSocket({ singleDate, getChartData }) {
  const { subscribe, send, getReadyState } = useDashboardWebSocket();
  const { isLiveEnabled, liveVersion } = useDashboardLive();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRecognitions, setTotalRecognitions] = useState(0);

  const hasDataRef = useRef(false);
  const usedRestFallbackRef = useRef(false);
  const activeDateRef = useRef(singleDate);
  const isLiveEnabledRef = useRef(isLiveEnabled);
  const pendingDateRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const getChartDataRef = useRef(getChartData);

  activeDateRef.current = singleDate;
  isLiveEnabledRef.current = isLiveEnabled;
  getChartDataRef.current = getChartData;

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const applyRecognitionPayload = useCallback((payload) => {
    if (payload?.type === "user_recognition_pie_error") {
      setError(payload.detail || "Unable to load recognition statistics");
      setLoading(false);
      return;
    }

    if (
      payload?.type === "user_recognition_pie_snapshot" ||
      payload?.type === "user_recognition_pie_update"
    ) {
      if (!payloadMatchesActiveDate(payload, activeDateRef.current)) {
        return;
      }

      hasDataRef.current = true;
      clearFallbackTimer();
      setTotalRecognitions(payload.total_recognitions || 0);
      setData(getChartDataRef.current(payload));
      setError(null);
      setLoading(false);
      return;
    }

    if (payload?.type === "dashboard_ws_error" && payload.code === 4401) {
      setError("Session expired. Please sign in again.");
      setLoading(false);
    }
  }, [clearFallbackTimer]);

  const fetchRecognitionRest = useCallback(async (requestedDate, options = {}) => {
    const { showLoading = true } = options;
    const targetDate = requestedDate || activeDateRef.current;

    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    hasDataRef.current = false;
    usedRestFallbackRef.current = false;
    clearFallbackTimer();

    try {
      const result = await getRecognitionPerDay(null, null, targetDate);
      applyRecognitionPayload({
        type: "user_recognition_pie_snapshot",
        ...result,
      });
    } catch (err) {
      console.error("[Recognition Pie] REST fetch failed:", err);
      setError(err.message || "Unable to load recognition statistics");
      setData([]);
      setTotalRecognitions(0);
      setLoading(false);
    }
  }, [applyRecognitionPayload, clearFallbackTimer]);

  const fetchRecognitionRestFallback = useCallback(async (requestedDate) => {
    const targetDate = requestedDate || activeDateRef.current;
    if (usedRestFallbackRef.current || hasDataRef.current) {
      return;
    }

    const readyState = getReadyState();
    if (readyState === WebSocket.CONNECTING || readyState === WebSocket.OPEN) {
      return;
    }

    usedRestFallbackRef.current = true;
    clearFallbackTimer();
    try {
      const result = await getRecognitionPerDay(null, null, targetDate);
      applyRecognitionPayload({
        type: "user_recognition_pie_snapshot",
        ...result,
      });
    } catch (err) {
      console.error("[Recognition Pie WS] REST fallback failed:", err);
      setError(err.message || "Unable to load recognition statistics");
      setLoading(false);
    }
  }, [applyRecognitionPayload, clearFallbackTimer, getReadyState]);

  const scheduleRestFallback = useCallback((requestedDate) => {
    clearFallbackTimer();
    fallbackTimerRef.current = setTimeout(() => {
      fallbackTimerRef.current = null;
      if (hasDataRef.current || usedRestFallbackRef.current) {
        return;
      }
      if (getReadyState() === WebSocket.OPEN) {
        return;
      }
      fetchRecognitionRestFallback(requestedDate || activeDateRef.current);
    }, RECOGNITION_PIE_WS_FALLBACK_MS);
  }, [clearFallbackTimer, fetchRecognitionRestFallback, getReadyState]);

  const sendPieDateOrQueue = useCallback((targetDate) => {
    if (send({ type: "set_pie_date", single_date: targetDate })) {
      pendingDateRef.current = null;
      return true;
    }

    if (getReadyState() === WebSocket.CONNECTING) {
      pendingDateRef.current = targetDate;
      return true;
    }

    return false;
  }, [getReadyState, send]);

  const refresh = useCallback((dateOverride, options = {}) => {
    const { showLoading = true } = options;
    const targetDate = dateOverride || activeDateRef.current;
    activeDateRef.current = targetDate;

    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    hasDataRef.current = false;
    usedRestFallbackRef.current = false;

    if (!isLiveEnabledRef.current) {
      fetchRecognitionRest(targetDate, { showLoading });
      return;
    }

    if (sendPieDateOrQueue(targetDate)) {
      scheduleRestFallback(targetDate);
      return;
    }

    fetchRecognitionRestFallback(targetDate);
  }, [fetchRecognitionRest, fetchRecognitionRestFallback, scheduleRestFallback, sendPieDateOrQueue]);

  useEffect(() => {
    hasDataRef.current = false;
    usedRestFallbackRef.current = false;
    activeDateRef.current = singleDate;
    setLoading(true);
    setError(null);

    const unsubscribe = subscribe((payload) => {
      if (payload?.type === "__dashboard_ws_open") {
        const targetDate = pendingDateRef.current || activeDateRef.current;
        pendingDateRef.current = null;
        if (targetDate) {
          send({ type: "set_pie_date", single_date: targetDate });
        }
        return;
      }
      if (!PIE_MESSAGE_TYPES.has(payload?.type) && payload?.type !== "dashboard_ws_error") {
        return;
      }
      applyRecognitionPayload(payload);
    });

    if (isLiveEnabled) {
      sendPieDateOrQueue(singleDate);
      scheduleRestFallback(singleDate);
    } else {
      fetchRecognitionRest(singleDate, { showLoading: true });
    }

    return () => {
      clearFallbackTimer();
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiveEnabled, liveVersion]);

  return {
    data,
    error,
    loading,
    refresh,
    totalRecognitions,
  };
}
